<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { infoBoxIdEnum } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import InfoBox from '@/components/shared/InfoBox.vue'

const i18n = getI18n()
const planningStore = usePlanningStore()
const { notifyWarning } = useNotify()

const { projectAllocationActionEnum } = planningStore

const show = ref(false)
const slotsOnly = ref(false)
let resolver = null

const slotsOptions = [
  { key: projectAllocationActionEnum.REDISTRIBUTE, label: i18n.t('common.redistributeAllocations'), icon: 'mdi-calendar-sync', color: 'primary', standalone: true },
  { key: projectAllocationActionEnum.KEEP, label: i18n.t('common.keepAllocations'), icon: 'mdi-calendar-lock', color: 'grey-7', standalone: true },
]

// Decide what to do with the existing service allocations when a project's dates/slots change.
// Resolves { action } (KEEP/REDISTRIBUTE, or null when nothing to ask), { blocked } (project in
// progress) or { cancelled }.
const confirm = ({ project, hasAllocations, datesChanged, slotsChanged }) => {
  if (!hasAllocations || !(datesChanged || slotsChanged)) return Promise.resolve({ action: null })
  if (project?.has_confirmed_movements) {
    notifyWarning(i18n.t('warning.projectInProgress'))
    return Promise.resolve({ blocked: true })
  }
  slotsOnly.value = !datesChanged
  show.value = true
  return new Promise((resolve) => { resolver = resolve })
}

const finish = (result) => {
  show.value = false
  const resolve = resolver
  resolver = null
  resolve?.(result)
}

const onConfirm = () => finish({ action: projectAllocationActionEnum.REDISTRIBUTE })
const onSlotsSelect = (key) => finish({ action: key })
const onCancel = () => finish({ cancelled: true })

defineExpose({ confirm })
</script>

<template>
  <confirm-dialog
    :model-value="show && !slotsOnly"
    :title="i18n.t('common.moveAllocationsTitle')"
    :message="i18n.t('common.moveAllocationsMessage')"
    icon="mdi-calendar-sync"
    icon-color="info"
    :label="i18n.t('common.redistributeAllocations')"
    @confirm="onConfirm"
    @cancel="onCancel"
    @update:model-value="val => { if (!val) onCancel() }"
  >
    <info-box :id="infoBoxIdEnum.MOVE_ALLOCATIONS" :message="i18n.t('common.moveAllocationsBanner')" />
  </confirm-dialog>
  <delete-options-dialog
    :model-value="show && slotsOnly"
    :title="i18n.t('common.updateSlotsTitle')"
    :message="i18n.t('common.updateSlotsMessage')"
    :caption="i18n.t('common.updateSlotsBanner')"
    icon="mdi-calendar-clock"
    icon-color="info"
    :options="slotsOptions"
    @select="onSlotsSelect"
    @cancel="onCancel"
    @update:model-value="val => { if (!val) onCancel() }"
  />
</template>
